import React, { useContext, useState } from 'react';
import { Box, Button, Checkbox, Grid, FormControlLabel, TextField, Typography, Divider, } from '@mui/material';
import { ThemeContext } from '../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoginApi, resetPasswordApi } from '../../../api/request/auth';
import toast from 'react-hot-toast';
import { setCookies } from '../../../services/Cookies';
import CryptoJS from "crypto-js";
import { setDecimalPlaces, setLoggedIn, setUserCompleteDetails, setUserDetails } from '../../../store/Slices/userSlice';
import CompanyDetail from '../Login/CompanyDetail';
import SignUp from './SignUp';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import UserName from './UserName';
import { addJournalLog } from '../../../components/tab/tabview/journal/addJournalLog';

export default function Login() {
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({ username: "", password: "" });
    const [savePassword, setSavePassword] = useState(false);
    const [isCompanyDetaildVisible, setIsCompanyDetailVisible] = useState(false);
    const [isForgetPasswordVisible, setForgetPasswordVisible] = useState(false)
    const [resetPassword, setResetPassword] = useState({ companyName: '', username: "", email: "" })
    const SECRET_KEY = process.env.REACT_APP_SECRET_KEY
    const [emailError, setEmailError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [isSignupVisible, setIsSignupVisible] = useState(false);
    const [isUserNameVisible, setIsUserNameVisible] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { username, password } = initialValues;

        toast.dismiss();

        if (!username && !password) {
            toast.error("Please enter username and password.");
            return;
        }
        if (!username) {
            toast.error("Please enter username.");
            return;
        }
        if (!password) {
            toast.error("Please enter password.");
            return;
        }
        try {
            setIsLoading(true);
            const params = { login_type: "api", ...initialValues };
            const { data } = await LoginApi(params);

            if (data?.token) {
                toast.success("Login successful!");
                setCookies("username", params?.username, { path: "/", secure: true, sameSite: "strict", });
                setCookies("token", data?.token, { path: "/", secure: true, sameSite: "strict", });
                setCookies("refreshToken", data?.refreshToken, { path: "/", secure: true, sameSite: "strict", });
                setCookies("clientId", data?.client?.id, { path: "/", secure: true, sameSite: "strict", });
                setCookies("gmtOffset", data?.gmtOffset, { path: "/", secure: true, sameSite: "strict", });
                setCookies('loginTime', new Date().getTime());
                // addJournalLog('login', `Account ${username}: ${accountId} is logged in successfully.`);
                localStorage.setItem("clientId", data?.client?.id);
                localStorage.setItem("username", params?.username);
                const tokenExpiry = data?.tokenExpiry || 60; // in minutes
                const expiryDuration = tokenExpiry * 60 * 1000; // convert to ms
                const bufferTime = 5 * 60 * 1000; // 5 minutes in ms
                const currentTime = new Date().getTime();
                const refreshTime = (currentTime + expiryDuration) - bufferTime;
                setCookies('refreshTime', refreshTime);
                if (savePassword) {
                    // console.log('before encryption', params.password, SECRET_KEY);
                    const iv = CryptoJS.lib.WordArray.random(16); // Generate a random IV
                    const encryptedPassword = CryptoJS.AES.encrypt(params.password, CryptoJS.enc.Utf8.parse(SECRET_KEY), { iv: iv }).toString();

                    // Save the IV and encrypted password as a single string
                    const encryptedData = iv.toString(CryptoJS.enc.Base64) + encryptedPassword;
                    // localStorage.setItem("password", encryptedData);
                    localStorage.setItem("savedCredentials", JSON.stringify({
                        username: params.username,
                        password: encryptedData
                    }));

                } else {
                    localStorage.removeItem("password");
                }
                dispatch(setLoggedIn(true));
                dispatch(setUserDetails(data?.client));
                dispatch(setUserCompleteDetails(data))
                dispatch(setDecimalPlaces(data?.decimalPlacesForAmountFormatting));
                navigate("/dashboard");
            } else {
                toast.error("Login failed. Please check your credentials.");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "An error occurred while logging in.";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompanyDetailsToggle = () => {
        setIsCompanyDetailVisible(!isCompanyDetaildVisible);
    };

    const handleForgetPasswordToggle = () => {
        setForgetPasswordVisible(!isForgetPasswordVisible);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setEmailError('');
        setUsernameError('');
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let hasError = false;
        if (!resetPassword.username || !resetPassword.email) {
            toast.error('Please fill out all fields!');
            return;
        }
        if (!emailRegex.test(resetPassword.email)) {
            setEmailError('Invalid Email');
            toast.error('Invalid Email');
            return;
        }
        if (!resetPassword.username) {
            setUsernameError('Username is required');
            hasError = true;
        } else if (resetPassword.username.length < 4) {
            setUsernameError('Username must be at least 4 characters');
            hasError = true;
        }

        setIsLoading(true);
        try {
            console.log("Resetting password with data:", resetPassword);
            const payload = { ...resetPassword, companyName: process.env.REACT_APP_COMPANY_NAME };
            await resetPasswordApi(payload);
            toast.success('Password reset successful');
            setResetPassword({ companyName: '', username: "", email: "" });
        } catch (error) {
            const apiMessage = error?.response?.data?.message || 'An error occurred while resetting the password';

            if (apiMessage.toLowerCase().includes('username')) {
                setUsernameError(apiMessage);
            } else if (apiMessage.toLowerCase().includes('email')) {
                setEmailError(apiMessage);
            }
            toast.error(apiMessage);
        } finally {
            setIsLoading(false);
        }
    };
    const savedCredentials = JSON.parse(localStorage.getItem("savedCredentials") || "{}");

    const storedName = savedCredentials.username;
    const savedPassword = savedCredentials.password;

    return (
        <>
            <Box sx={{ width: '100%', mt: 1 }}>
                {!isSignupVisible && !isForgetPasswordVisible && !isCompanyDetaildVisible && !isUserNameVisible && (
                    <>
                        <Typography variant="h6" fontWeight={600} sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t("Welcomeback")}</Typography>
                        <Typography variant="body2" sx={{ color: 'gray', mb: 3, fontSize: "0.85rem" }}>{t('signUpText')}</Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Grid container direction="column">
                                    <Grid item><Typography variant="body2" sx={{ mb: 0.5, textAlign: 'left', color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('username')}</Typography></Grid>
                                    <Grid item>
                                        <TextField name="username" placeholder={t('username')} fullWidth size="small" required value={initialValues.username} onChange={(e) => setInitialValues({ ...initialValues, username: e.target.value })} className="custom-textfield" />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container direction="column">
                                    <Grid item><Typography variant="body2" sx={{ mb: 0.5, textAlign: 'left', color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('enterPassword')}</Typography></Grid>
                                    <Grid item>
                                        <TextField id="outlined-basic" placeholder={t("enterPassword")} variant="outlined" name="password" type="password" fullWidth size="small" required value={initialValues.password} onChange={(e) => setInitialValues({ ...initialValues, password: e.target.value })} className="custom-textfield" />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mt: 1, mb: 1, }}>
                            <FormControlLabel control={<Checkbox className="custom-checkbox" onChange={(e) => { setSavePassword(e.target.checked); console.log("Save password checked:", e.target.checked); }} />}
                                label={t('savePassword')} sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" }, color: darkMode ? "var(--lightText)" : 'var(--black)', }}
                            />
                        </Box>
                        <Grid container alignItems="center" sx={{ marginBottom: "15px" }}>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={5} sm={4}><Typography className="text-gray1" sx={{ fontSize: "0.775rem", textAlign: "right", marginRight: "5px", cursor: "pointer", }} onClick={handleForgetPasswordToggle}>{t('forgotPassword')}</Typography></Grid>
                            <Grid item xs={5} sm={4}><Typography className="text-primary" sx={{ fontSize: "0.775rem", textAlign: "left", cursor: "pointer", marginLeft: "5px", }} onClick={handleCompanyDetailsToggle}>{t('contactCompany')}</Typography></Grid>
                        </Grid>

                        {/* <Button variant="contained" onClick={handleSubmit} fullWidth sx={{
                            py: 1.2, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '1rem', backgroundImage: darkMode ? 'linear-gradient(to bottom, #1e293b, #0f1720)' : 'linear-gradient(to bottom, #232323, #0f0f0f)', color: '#fff', boxShadow: darkMode ? '0 2px 4px rgba(0,0,0,0.6)' : '0 2px 4px rgba(0,0,0,0.2)',
                            '&:hover': { backgroundImage: darkMode ? 'linear-gradient(to bottom, #334155, #1e293b)' : 'linear-gradient(to bottom, #3a3a3a, #2a2a2a)', boxShadow: darkMode ? '0 3px 6px rgba(0,0,0,0.7)' : '0 3px 6px rgba(0,0,0,0.3)', }
                        }}> */}
                        <Button fullWidth variant="contained" color="secondary" onClick={handleSubmit} sx={{ border: "1px solid green", textTransform: "none", padding: "5px", color: '#fff' }}>  {t('connectToAccount')}</Button>
                        <Divider sx={{ p: 0.5 }} />
                        {storedName && savedPassword && (<Button onClick={() => setIsUserNameVisible(true)} fullWidth variant='outlined' color="secondary" sx={{ border: "1px solid green", textTransform: "uppercase", padding: "5px", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, }}> <FiberManualRecordIcon sx={{ fontSize: 13 }} />
                            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>{storedName}</Box></Button>)}
                        <Typography variant="body2" sx={{ mt: 2, color: darkMode ? "var(--lightText)" : 'var(--black)', fontSize: "0.85rem" }}>{t('dontHaveaccount')}<Typography onClick={() => setIsSignupVisible(true)} component="span" sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', fontSize: "0.85rem" }}> {t('dontHaveaccount2')}</Typography></Typography>
                    </>
                )}

                {isCompanyDetaildVisible && (
                    <CompanyDetail onBackToLogin={handleCompanyDetailsToggle} />
                )}
                {isForgetPasswordVisible && (
                    <>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}><Typography variant="h5" fontWeight={700} sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('resetPassword')}</Typography></Grid>
                            <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('email')}</Typography></Grid>
                            <Grid item xs={9}><TextField value={resetPassword.email} onChange={(e) => setResetPassword({ ...resetPassword, email: e.target.value })} name="email" type="email" placeholder={t('enterEmail')} fullWidth size="small" required className="custom-textfield" error={Boolean(emailError)} helperText={emailError} /></Grid>
                            <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('username')}</Typography></Grid>
                            <Grid item xs={9}><TextField value={resetPassword.username} onChange={(e) => setResetPassword({ ...resetPassword, username: e.target.value })} name="username" type="text" placeholder={t("enterUsername")} fullWidth size="small" required className="custom-textfield" error={Boolean(usernameError)} helperText={usernameError} /></Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={8} sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                                <Button type="submit" fullWidth variant="contained" onClick={handleResetPassword} color="secondary" sx={{ border: "1px solid green", color: "white", padding: "5px", textTransform: "none", }}>{t('resetPassword')}</Button>
                            </Grid>

                            <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <Button fullWidth variant="outlined" color="secondary" onClick={() => setForgetPasswordVisible(false)} sx={{ border: "1px solid green", textTransform: "none", padding: "5px", }}>{t('cancel')}</Button>
                            </Grid>
                        </Grid>

                    </>
                )}
                {isSignupVisible && (
                    <SignUp back={() => setIsSignupVisible(false)} />
                )}
                {isUserNameVisible && (<UserName back={() => setIsUserNameVisible(false)} />)}
            </Box >
        </>
    );
}
